import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import {
  getFlight, updateFlightData, saveDeicing, saveArrivalFuel, saveAutoland,
  addDelay, removeDelay, revokeAcceptance, closeFlight, cancelFlight, getComplaints
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import flightDataIcon from '../assets/icons/flightdata.svg';
import fuelUpliftIcon from '../assets/icons/fueluplift.svg';
import deicingIcon from '../assets/icons/deicing.svg';
import delayIcon from '../assets/icons/delay.svg';
import aircraftStatusIcon from '../assets/icons/aircraftstatus.svg';
import cabinItemsIcon from '../assets/icons/cabinitems.svg';
import openComplaintsIcon from '../assets/icons/opencomplaints.svg';
import arrowLeftIcon from '../assets/icons/arrow-sm-left.svg';
import { DELAY_CODE_OPTIONS } from '../constants/delayCodes';
import AircraftStatusReport from './AircraftStatusReport';
import CommanderConfirmationModal from './CommanderConfirmationModal';
import './OnGroundMode.css';

const FUEL_TANKS = ['left', 'center', 'right', 'other'];

const toFuelNumber = value => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const formatFuelValue = value => {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const formatMinutesToHourMin = (minutes) => {
  const totalMinutes = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins}m`;
};

const toValidDate = value => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct;
  if (typeof value === 'string') {
    const normalized = value.includes(' ') ? value.replace(' ', 'T') : value;
    const fallback = new Date(normalized);
    if (!Number.isNaN(fallback.getTime())) return fallback;
  }
  return null;
};

const formatDateSafe = (value, pattern) => {
  const date = toValidDate(value);
  return date ? format(date, pattern) : '';
};

const formatDisplayDate = (value) => {
  const date = toValidDate(value);
  return date ? format(date, 'dd.MMM.yyyy') : '';
};

const formatDisplayDateTime = (value) => {
  const date = toValidDate(value);
  return date ? format(date, 'dd.MMM.yyyy HH:mm') : '';
};

const formatToZulu = (value) => {
  const date = toValidDate(value);
  if (!date) return '';
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}Z`;
};

const handleZuluTimeInput = (value) => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
  if (digitsOnly.length === 0) return '';
  if (digitsOnly.length <= 2) {
    const hh = Math.min(Number.parseInt(digitsOnly, 10) || 0, 24);
    return String(hh).padStart(digitsOnly.length, '0');
  }
  if (digitsOnly.length === 3) {
    const hh = Math.min(Number.parseInt(digitsOnly.slice(0, 2), 10) || 0, 24);
    const minuteTens = hh === 24 ? '0' : digitsOnly.slice(2);
    return `${String(hh).padStart(2, '0')}:${minuteTens}`;
  }
  const rawHours = Number.parseInt(digitsOnly.slice(0, 2), 10) || 0;
  const rawMinutes = Number.parseInt(digitsOnly.slice(2), 10) || 0;
  const hours = Math.min(rawHours, 24);
  let minutes = Math.min(rawMinutes, 59);
  if (hours === 24 && minutes > 0) minutes = 0;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}Z`;
};

const formatIcaoInput = (value) => value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
const DEICING_FLUID_TYPES = ['TYPE I', 'TYPE II', 'TYPE IV'];
const ROW_EXIT_ANIMATION_MS = 240;
const createEmptyDeicingRecord = (recordNumber = 1) => ({
  record_number: recordNumber,
  fluid_type: '',
  mixture: '',
  quantity: '',
  start_utc: '',
  holdover_min: '',
  holdover_max: ''
});

const AUTOLAND_STATUS = {
  NO: 'no',
  SUCCESSFUL: 'successful',
  FAILED: 'failed'
};

const AUTOLAND_WIND_REGEX = /^(?:[0-2]\d{2}|3[0-5]\d|VRB)\/\d{2}$/i;
const AUTOLAND_TIME_REGEX = /^(?:[01]\d|2[0-4]):[0-5]\dZ$/;
const AUTOLAND_TEMP_REGEX = /^-?\d{2}C$/i;

const formatAutolandQnhInput = (value) => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
const AUTOLAND_RUNWAY_REGEX = /^\d{2}[CRL]?$/i;

const formatAutolandRunwayInput = (value) => {
  const normalized = value.toUpperCase().replace(/[^0-9CRL]/g, '');
  const digits = normalized.replace(/[^0-9]/g, '').slice(0, 2);
  const suffix = normalized.replace(/[0-9]/g, '').replace(/[^CRL]/g, '').slice(0, 1);
  return `${digits}${digits.length === 2 ? suffix : ''}`;
};

const formatAutolandWindInput = (value) => {
  const normalized = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!normalized) return '';

  if (normalized === 'V' || normalized === 'VR') return normalized;

  if (normalized.startsWith('VRB')) {
    const speed = normalized.slice(3).replace(/\D/g, '').slice(0, 2);
    return speed.length ? `VRB/${speed}` : 'VRB/';
  }

  const digits = normalized.replace(/\D/g, '').slice(0, 5);
  if (!digits) return '';

  if (digits.length < 3) return digits;

  const heading = String(Math.min(Number.parseInt(digits.slice(0, 3), 10) || 0, 359)).padStart(3, '0');
  if (digits.length === 3) return `${heading}/`;
  return `${heading}/${digits.slice(3, 5)}`;
};

const formatAutolandTempInput = (value) => {
  const upper = value.toUpperCase();
  const isNegative = upper.trim().startsWith('-');
  const digits = upper.replace(/\D/g, '').slice(0, 2);
  if (!digits.length) return isNegative ? '-' : '';
  if (digits.length < 2) return `${isNegative ? '-' : ''}${digits}`;
  return `${isNegative ? '-' : ''}${digits}C`;
};

const createDefaultAutolandReport = () => ({
  time: '',
  runway: '',
  wind: '',
  qnh: '',
  temp: '',
  vis_rvr: '',
  td_point: '',
  alignment: '',
  landing: '',
  tracking: '',
  speed_control: '',
  ac_dropout: '',
  other: '',
  reasons: ''
});

const createDefaultAutoland = () => ({
  status: AUTOLAND_STATUS.NO,
  performed: false,
  autoland_count: 0,
  report: createDefaultAutolandReport()
});

const Chevron = () => <span className="ogm-chevron" aria-hidden="true">›</span>;

const TaskRow = ({ label, statusText, isComplete, onClick, rightElement, noChevron, iconSrc, neutral = false, iconClassName = '' }) => (
  <div className={`ogm-item ${neutral ? '' : (isComplete ? 'complete' : 'incomplete')}`} onClick={onClick}>
    <div className="ogm-left">
      <span className="ogm-icon-placeholder" aria-hidden="true">
        {iconSrc && <img src={iconSrc} alt="" className={`ogm-icon-img ${iconClassName}`.trim()} />}
      </span>
      <span>{label}</span>
    </div>
    <div className="ogm-right">
      {statusText && <span className="ogm-status-text">{statusText}</span>}
      {rightElement}
      {!noChevron && <Chevron />}
    </div>
  </div>
);

// Max fuel capacity by aircraft type
const MAX_FUEL_BY_TYPE = {
  'A321-271N': 18970,
  'A321-231': 18644,
  'A320-232': 19004,
  'A320-271N': 18560
};

const getMaxFuelForAircraft = (acType) => {
  if (!acType) return 20000;
  if (MAX_FUEL_BY_TYPE[acType]) return MAX_FUEL_BY_TYPE[acType];
  for (const [type, maxFuel] of Object.entries(MAX_FUEL_BY_TYPE)) {
    if (acType.includes(type) || type.includes(acType)) {
      return maxFuel;
    }
  }
  if (acType.includes('A321')) return 18644;
  if (acType.includes('A320')) return 18560;
  return 20000;
};

const PostFlightMode = ({ flightId, onBack, onRevoke }) => {
  const { aircraft } = useAuth();
  const [view, setView] = useState('main');
  const [viewTransitionDirection, setViewTransitionDirection] = useState('none');
  const [flight, setFlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Forms
  const [flightData, setFlightData] = useState({
    to_airport: '',
    time_off_block: '', time_takeoff: '', time_landing: '', time_on_block: '',
    full_stop_landings: 0, touch_and_go: 0, total_landings: 0,
    edto: false, pushback: false
  });
  const [zuluTimes, setZuluTimes] = useState({
    time_off_block: '',
    time_takeoff: '',
    time_landing: '',
    time_on_block: ''
  });
  const [arrivalFuel, setArrivalFuel] = useState({
    arrival_fuel_left: '', arrival_fuel_center: '', arrival_fuel_right: '', arrival_fuel_other: ''
  });
  const [autoland, setAutoland] = useState(createDefaultAutoland());
  const [deicingRecords, setDeicingRecords] = useState([createEmptyDeicingRecord(1)]);
  const [deicingFluidDropdownOpenIndex, setDeicingFluidDropdownOpenIndex] = useState(null);
  const [delay, setDelay] = useState({ delay_code: '', delay_description: '', delay_minutes: '' });
  const [delayDropdownOpen, setDelayDropdownOpen] = useState(false);
  const delayDropdownRef = useRef(null);
  const scrollPositionsRef = useRef({});
  const [removingDeicingIndex, setRemovingDeicingIndex] = useState(null);
  const [removingDelayId, setRemovingDelayId] = useState(null);
  const [animateDeicingAddBtn, setAnimateDeicingAddBtn] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [complaints, setComplaints] = useState([]);

  const viewContainerClass = [
    'ogm-container',
    viewTransitionDirection === 'none' ? '' : `ogm-view-${viewTransitionDirection}`,
    view !== 'main' ? 'ogm-subview-overlay' : ''
  ].filter(Boolean).join(' ');

  useEffect(() => {
    const dashboardLayout = document.querySelector('.dashboard-layout');
    if (!dashboardLayout) return undefined;

    if (view !== 'main') {
      dashboardLayout.classList.add('dashboard-subview-open');
    } else {
      dashboardLayout.classList.remove('dashboard-subview-open');
    }

    return () => {
      dashboardLayout.classList.remove('dashboard-subview-open');
    };
  }, [view]);

  const captureCurrentScrollPosition = useCallback(() => {
    const scrollArea = document.querySelector('.ogm-container .ogm-scroll-area');
    if (scrollArea) {
      scrollPositionsRef.current[view] = scrollArea.scrollTop;
    }
  }, [view]);

  const openSubView = useCallback((nextView) => {
    captureCurrentScrollPosition();
    setViewTransitionDirection('forward');
    setView(nextView);
  }, [captureCurrentScrollPosition]);

  const navigateBackToMain = useCallback(() => {
    captureCurrentScrollPosition();
    setViewTransitionDirection('back');
    setView('main');
  }, [captureCurrentScrollPosition]);

  const formatLoadedFuel = (val, defaultVal = '') => {
    if (val == null) return defaultVal;
    const num = Number(val);
    return String(parseFloat(num.toFixed(10)));
  };

  const loadFlight = useCallback(async () => {
    if (!flightId) return;
    setLoading(true);
    try {
      const res = await getFlight(flightId);
      setFlight(res.data);
      const f = res.data.flight;
      setFlightData({
        to_airport: f.to_airport || '',
        time_off_block: formatDateSafe(f.time_off_block, "yyyy-MM-dd'T'HH:mm"),
        time_takeoff: formatDateSafe(f.time_takeoff, "yyyy-MM-dd'T'HH:mm"),
        time_landing: formatDateSafe(f.time_landing, "yyyy-MM-dd'T'HH:mm"),
        time_on_block: formatDateSafe(f.time_on_block, "yyyy-MM-dd'T'HH:mm"),
        full_stop_landings: f.full_stop_landings || 0,
        touch_and_go: f.touch_and_go || 0,
        total_landings: f.total_landings || 0,
        edto: f.edto || false,
        pushback: f.pushback || false
      });
      setZuluTimes({
        time_off_block: formatToZulu(f.time_off_block),
        time_takeoff: formatToZulu(f.time_takeoff),
        time_landing: formatToZulu(f.time_landing),
        time_on_block: formatToZulu(f.time_on_block)
      });
      if (res.data.arrivalFuel) {
        const af = res.data.arrivalFuel;
        setArrivalFuel({
          arrival_fuel_left: formatLoadedFuel(af.arrival_fuel_left),
          arrival_fuel_center: formatLoadedFuel(af.arrival_fuel_center),
          arrival_fuel_right: formatLoadedFuel(af.arrival_fuel_right),
          arrival_fuel_other: formatLoadedFuel(af.arrival_fuel_other)
        });
      }
      if (res.data.autoland) {
        const dbAutoland = res.data.autoland;
        const explicitStatus = [AUTOLAND_STATUS.NO, AUTOLAND_STATUS.SUCCESSFUL, AUTOLAND_STATUS.FAILED]
          .includes(dbAutoland.status)
          ? dbAutoland.status
          : null;
        const fallbackStatus = dbAutoland.performed ? AUTOLAND_STATUS.SUCCESSFUL : AUTOLAND_STATUS.NO;
        setAutoland({
          status: explicitStatus || fallbackStatus,
          performed: !!dbAutoland.performed,
          autoland_count: dbAutoland.autoland_count || 0,
          report: {
            time: dbAutoland.report_time || '',
            runway: dbAutoland.runway || '',
            wind: dbAutoland.wind || '',
            qnh: dbAutoland.qnh || '',
            temp: dbAutoland.temp || '',
            vis_rvr: dbAutoland.vis_rvr || '',
            td_point: dbAutoland.td_point || '',
            alignment: dbAutoland.alignment || '',
            landing: dbAutoland.landing || '',
            tracking: dbAutoland.tracking || '',
            speed_control: dbAutoland.speed_control || '',
            ac_dropout: dbAutoland.ac_dropout || '',
            other: dbAutoland.other_data || '',
            reasons: dbAutoland.reasons || ''
          }
        });
      } else {
        setAutoland(createDefaultAutoland());
      }
      if (Array.isArray(res.data.deicingRecords) && res.data.deicingRecords.length > 0) {
        setDeicingRecords(res.data.deicingRecords.map((record, idx) => ({
          record_number: record.record_number || (idx + 1),
          fluid_type: record.fluid_type || '',
          mixture: record.mixture || record.concentration || '',
          quantity: record.quantity != null ? String(record.quantity) : '',
          start_utc: record.start_utc || formatToZulu(record.performed_at),
          holdover_min: record.holdover_min != null ? String(record.holdover_min) : '',
          holdover_max: record.holdover_max != null ? String(record.holdover_max) : ''
        })));
      } else if (res.data.deicing) {
        setDeicingRecords([{
          record_number: 1,
          fluid_type: res.data.deicing.fluid_type || '',
          mixture: res.data.deicing.concentration || '',
          quantity: res.data.deicing.quantity != null ? String(res.data.deicing.quantity) : '',
          start_utc: formatToZulu(res.data.deicing.performed_at),
          holdover_min: '',
          holdover_max: ''
        }]);
      } else {
        setDeicingRecords([createEmptyDeicingRecord(1)]);
      }
      if (Array.isArray(res.data.delays) && res.data.delays.length > 0) {
        const latestDelay = [...res.data.delays].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
        setDelay(prev => prev.delay_code ? prev : {
          ...prev,
          delay_code: String(latestDelay.delay_code || '')
        });
      }
    } catch (err) {
      console.error('Error loading flight:', err);
    }
    setLoading(false);
  }, [flightId]);

  useEffect(() => {
    loadFlight();
  }, [loadFlight]);

  useEffect(() => {
    if (!aircraft) return;
    getComplaints(aircraft.id).then(r => setComplaints(r.data.complaints || [])).catch(() => {});
  }, [aircraft]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (delayDropdownRef.current && !delayDropdownRef.current.contains(event.target)) {
        setDelayDropdownOpen(false);
      }
      if (!event.target.closest('.deicing-type-dropdown')) {
        setDeicingFluidDropdownOpenIndex(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useLayoutEffect(() => {
    const scrollArea = document.querySelector('.ogm-container .ogm-scroll-area');
    if (!scrollArea) return undefined;

    const targetTop = scrollPositionsRef.current[view] || 0;
    const previousBehavior = scrollArea.style.scrollBehavior;
    scrollArea.style.scrollBehavior = 'auto';
    if (Math.abs(scrollArea.scrollTop - targetTop) > 1) {
      scrollArea.scrollTop = targetTop;
    }
    scrollArea.style.scrollBehavior = previousBehavior;

    const onScroll = () => {
      scrollPositionsRef.current[view] = scrollArea.scrollTop;
    };

    scrollArea.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollArea.removeEventListener('scroll', onScroll);
    };
  }, [view]);

  const maxFuelCapacity = getMaxFuelForAircraft(aircraft?.ac_type);

  const arrivalFuelTotal = FUEL_TANKS.reduce((sum, tank) => sum + toFuelNumber(arrivalFuel[`arrival_fuel_${tank}`]), 0);

  const hasFlightData = !!flightData.time_on_block;
  const hasArrivalFuel = arrivalFuel.arrival_fuel_left !== '' && arrivalFuel.arrival_fuel_center !== '' &&
                         arrivalFuel.arrival_fuel_right !== '' && arrivalFuel.arrival_fuel_other !== '';

  const handleSaveFlightData = async () => {
    setSaving(true);
    try {
      await updateFlightData(flightId, flightData);
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleLandingCountChange = (field, rawValue) => {
    const nextValue = parseInt(rawValue, 10) || 0;
    setFlightData(prev => {
      const next = {
        ...prev,
        [field]: nextValue
      };
      next.total_landings = (parseInt(next.full_stop_landings, 10) || 0) + (parseInt(next.touch_and_go, 10) || 0);
      return next;
    });
  };

  const handleSaveArrivalFuel = async () => {
    setSaving(true);
    try {
      const toFuelValue = (val) => {
        if (val === '' || val === null || val === undefined) return null;
        return toFuelNumber(val);
      };
      await saveArrivalFuel(flightId, {
        arrival_fuel_left: toFuelValue(arrivalFuel.arrival_fuel_left),
        arrival_fuel_center: toFuelValue(arrivalFuel.arrival_fuel_center),
        arrival_fuel_right: toFuelValue(arrivalFuel.arrival_fuel_right),
        arrival_fuel_other: toFuelValue(arrivalFuel.arrival_fuel_other)
      });
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleSaveAutoland = async () => {
    setSaving(true);
    try {
      await saveAutoland(flightId, {
        status: autoland.status,
        performed: autoland.status !== AUTOLAND_STATUS.NO,
        autoland_count: autoland.status === AUTOLAND_STATUS.NO ? 0 : (autoland.autoland_count || 1),
        report_time: autoland.report.time,
        runway: autoland.report.runway,
        wind: autoland.report.wind,
        qnh: autoland.report.qnh,
        temp: autoland.report.temp,
        vis_rvr: autoland.report.vis_rvr,
        td_point: autoland.report.td_point,
        alignment: autoland.report.alignment,
        landing: autoland.report.landing,
        tracking: autoland.report.tracking,
        speed_control: autoland.report.speed_control,
        ac_dropout: autoland.report.ac_dropout,
        other_data: autoland.report.other,
        reasons: autoland.report.reasons
      });
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleSaveDeicing = async () => {
    setSaving(true);
    try {
      await saveDeicing(flightId, {
        records: deicingRecords.map((record, idx) => ({
          record_number: idx + 1,
          fluid_type: record.fluid_type || '',
          mixture: record.mixture || null,
          quantity: record.quantity === '' ? null : Number(record.quantity),
          start_utc: record.start_utc || null,
          holdover_min: record.holdover_min === '' ? null : Number(record.holdover_min),
          holdover_max: record.holdover_max === '' ? null : Number(record.holdover_max)
        }))
      });
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleClose = async () => {
    setSaving(true);
    try {
      await closeFlight(flightId);
      setCloseModalOpen(false);
      onBack();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleRevoke = async () => {
    if (!window.confirm('Revoke aircraft acceptance? You will return to the On Ground page.')) return;
    setSaving(true);
    try {
      await revokeAcceptance(flightId);
      onRevoke();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this flight?')) return;
    try {
      await cancelFlight(flightId);
      onBack();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className={viewContainerClass}>
        <div className="spinner" style={{ margin: 'auto' }} />
      </div>
    );
  }

  const f = flight?.flight;
  const canEditOnBlock = !!flightData.time_off_block;
  const canEditTakeoff = !!flightData.time_on_block;
  const canEditLanding = !!flightData.time_takeoff;
  const getOooIInputClass = (canEdit, value) => {
    if (!canEdit) return 'fd-time-input-wrap fd-time-disabled';
    return `fd-time-input-wrap fd-time-active ${value ? 'complete' : 'incomplete'}`;
  };

  // Handler for Zulu time input changes
  const handleZuluChange = (field, value) => {
    const formatted = handleZuluTimeInput(value);
    setZuluTimes(prev => ({ ...prev, [field]: formatted }));
    if (formatted.length === 6 && formatted.endsWith('Z')) {
      const hours = parseInt(formatted.slice(0, 2), 10);
      const minutes = parseInt(formatted.slice(3, 5), 10);
      const baseDate = flightData[field] ? new Date(flightData[field]) : new Date();
      baseDate.setUTCHours(hours, minutes, 0, 0);
      setFlightData(d => ({ ...d, [field]: format(baseDate, "yyyy-MM-dd'T'HH:mm") }));
    } else {
      setFlightData(d => ({ ...d, [field]: '' }));
    }
  };

  const handleZuluKeyDown = (field, e) => {
    if (e.key !== 'Backspace') return;
    const current = zuluTimes[field] || '';
    const isCursorAtEnd = e.target.selectionStart === current.length && e.target.selectionEnd === current.length;
    if (!current.endsWith('Z') || !isCursorAtEnd) return;
    e.preventDefault();
    const digits = current.replace(/\D/g, '').slice(0, 4);
    const nextDigits = digits.slice(0, -1);
    const nextFormatted = handleZuluTimeInput(nextDigits);
    setZuluTimes(prev => ({ ...prev, [field]: nextFormatted }));
    setFlightData(d => ({ ...d, [field]: '' }));
    requestAnimationFrame(() => {
      e.target.setSelectionRange(nextFormatted.length, nextFormatted.length);
    });
  };

  const updateDeicingRecord = (index, patch) => {
    setDeicingRecords(prev => prev.map((record, idx) => (
      idx === index ? { ...record, ...patch } : record
    )));
  };

  const handleDeicingMinutesInput = (value) => value.replace(/\D/g, '').slice(0, 3);

  const handleDeicingNumericInput = (value) => {
    if (value === '') return '';
    if (value === '.') return '0.';
    if (!/^\d*\.?\d*$/.test(value)) return null;
    return value;
  };

  const handleDeicingZuluChange = (index, value) => {
    updateDeicingRecord(index, { start_utc: handleZuluTimeInput(value) });
  };

  const handleDeicingZuluKeyDown = (index, e) => {
    if (e.key !== 'Backspace') return;

    const current = deicingRecords[index]?.start_utc || '';
    const isCursorAtEnd = e.target.selectionStart === current.length && e.target.selectionEnd === current.length;
    if (!current.endsWith('Z') || !isCursorAtEnd) return;

    e.preventDefault();
    const digits = current.replace(/\D/g, '').slice(0, 4);
    const nextDigits = digits.slice(0, -1);
    const nextFormatted = handleZuluTimeInput(nextDigits);
    updateDeicingRecord(index, { start_utc: nextFormatted });
    requestAnimationFrame(() => {
      e.target.setSelectionRange(nextFormatted.length, nextFormatted.length);
    });
  };

  const addDeicingRecord = () => {
    setDeicingRecords(prev => [...prev, createEmptyDeicingRecord(prev.length + 1)]);
    setAnimateDeicingAddBtn(true);
    setTimeout(() => setAnimateDeicingAddBtn(false), 240);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollArea = document.querySelector('.ogm-scroll-area');
        scrollArea?.scrollTo({ top: scrollArea.scrollHeight, behavior: 'smooth' });
      });
    });
  };

  const removeDeicingRecord = (indexToRemove) => {
    if (removingDeicingIndex !== null) return;
    if (deicingRecords.length <= 1 || indexToRemove <= 0) return;
    setRemovingDeicingIndex(indexToRemove);
    setTimeout(() => {
      setDeicingRecords(prev => (
        prev
          .filter((_, idx) => idx !== indexToRemove)
          .map((record, idx) => ({ ...record, record_number: idx + 1 }))
      ));
      setRemovingDeicingIndex(null);
    }, ROW_EXIT_ANIMATION_MS);
  };

  const handleRemoveDelay = async (delayId) => {
    if (removingDelayId !== null) return;
    setRemovingDelayId(delayId);
    await new Promise(resolve => setTimeout(resolve, ROW_EXIT_ANIMATION_MS));
    try {
      await removeDelay(flightId, delayId);
      await loadFlight();
    } catch (err) {
      console.error(err);
    }
    setRemovingDelayId(null);
  };

  const handleDelayMinutesInput = (value) => value.replace(/\D/g, '').slice(0, 3);
  const isDelayMinutesValid = delay.delay_minutes !== '' && Number(delay.delay_minutes) >= 0 && Number(delay.delay_minutes) <= 999;
  const selectedDelayOption = DELAY_CODE_OPTIONS.find(option => option.code === delay.delay_code);
  const selectedDelayLabel = selectedDelayOption
    ? `${selectedDelayOption.code} (${selectedDelayOption.reason})`
    : 'Select delay code';

  // Numeric input handler for fuel
  const handleNumericInput = (value, setter) => {
    if (value === '' || value === '.' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
      setter(value);
    }
  };

  const updateArrivalFuelField = (field, value) => {
    setArrivalFuel(prev => {
      const newState = { ...prev, [field]: value };
      const total = FUEL_TANKS.reduce((sum, tank) => sum + toFuelNumber(newState[`arrival_fuel_${tank}`]), 0);
      if (total > maxFuelCapacity) {
        const otherFieldsTotal = total - toFuelNumber(value);
        const maxAllowedForThisField = maxFuelCapacity - otherFieldsTotal;
        newState[field] = maxAllowedForThisField > 0 ? String(Math.max(0, maxAllowedForThisField)) : '0';
      }
      return newState;
    });
  };

  const isFieldValid = (value) => value !== '' && value !== null && value !== undefined;
  const hasAutolandReport = autoland.status === AUTOLAND_STATUS.SUCCESSFUL || autoland.status === AUTOLAND_STATUS.FAILED;
  const autolandTimeIsValid = AUTOLAND_TIME_REGEX.test(autoland.report.time);
  const autolandRunwayIsValid = AUTOLAND_RUNWAY_REGEX.test(autoland.report.runway);
  const autolandWindIsValid = AUTOLAND_WIND_REGEX.test(autoland.report.wind);
  const autolandTempIsValid = AUTOLAND_TEMP_REGEX.test(autoland.report.temp);
  const autolandQnhIsValid = (autoland.report.qnh.match(/\d/g) || []).length >= 3;
  const hasAutolandComplete = autoland.status === AUTOLAND_STATUS.NO ||
    (hasAutolandReport && autolandTimeIsValid && autolandRunwayIsValid &&
      autolandWindIsValid && autolandQnhIsValid && autolandTempIsValid);
  const canCloseFlight = hasFlightData && hasAutolandComplete && hasArrivalFuel;

  const getAutolandFieldClass = (isComplete) => `autoland-field-control ${isComplete ? 'complete' : 'incomplete'}`;
  const hasValue = (value) => value !== '' && value !== null && value !== undefined;

  const updateAutolandReport = (field, value) => {
    setAutoland(prev => ({
      ...prev,
      report: {
        ...prev.report,
        [field]: value
      }
    }));
  };

  const setAutolandStatus = (status, { openReport = false } = {}) => {
    setAutoland(prev => ({
      ...prev,
      status,
      performed: status !== AUTOLAND_STATUS.NO,
      autoland_count: status === AUTOLAND_STATUS.NO ? 0 : (prev.autoland_count || 1)
    }));
    if (openReport && status !== AUTOLAND_STATUS.NO) {
      openSubView('autoland');
    }
  };

  const handleAutolandWindKeyDown = (e) => {
    if (e.key !== 'Backspace') return;
    const current = autoland.report.wind || '';
    const isCursorAtEnd = e.target.selectionStart === current.length && e.target.selectionEnd === current.length;
    if (!isCursorAtEnd || !current.endsWith('/')) return;
    e.preventDefault();
    const stripped = current.slice(0, -1);
    const reduced = stripped.slice(0, -1);
    updateAutolandReport('wind', formatAutolandWindInput(reduced));
  };

  const handleAutolandTempKeyDown = (e) => {
    if (e.key !== 'Backspace') return;
    const current = autoland.report.temp || '';
    const isCursorAtEnd = e.target.selectionStart === current.length && e.target.selectionEnd === current.length;
    if (!isCursorAtEnd || !current.endsWith('C')) return;
    e.preventDefault();
    const isNegative = current.startsWith('-');
    const digits = current.replace(/\D/g, '').slice(0, 2);
    const nextDigits = digits.slice(0, -1);
    const nextRaw = `${isNegative ? '-' : ''}${nextDigits}`;
    updateAutolandReport('temp', formatAutolandTempInput(nextRaw));
  };

  const crewMembers = (flight?.crew || []).filter(member => (member?.full_name || '').trim() !== '');
  const usedCrewIndexes = new Set();
  const captainIndex = crewMembers.findIndex(member => /(captain|cpt|pic|commander)/i.test(member.title || ''));
  if (captainIndex >= 0) usedCrewIndexes.add(captainIndex);
  const firstOfficerIndex = crewMembers.findIndex((member, idx) =>
    idx !== captainIndex && /(first officer|fo\b|co[- ]?pilot|copilot)/i.test(member.title || '')
  );
  if (firstOfficerIndex >= 0) usedCrewIndexes.add(firstOfficerIndex);
  const fallbackCaptainIndex = captainIndex >= 0 ? captainIndex : 0;
  if (fallbackCaptainIndex >= 0) usedCrewIndexes.add(fallbackCaptainIndex);
  const fallbackFirstOfficerIndex = firstOfficerIndex >= 0
    ? firstOfficerIndex
    : crewMembers.findIndex((_, idx) => !usedCrewIndexes.has(idx));
  const captainName = crewMembers[fallbackCaptainIndex]?.full_name || '';
  const firstOfficerName = crewMembers[fallbackFirstOfficerIndex]?.full_name || '';
  const autolandRegAndFlight = `${aircraft?.registration || ''}${flight?.flight?.flight_number ? ` / ${flight.flight.flight_number}` : ''}`;
  const autolandAirport = flight?.flight?.to_airport || '';
  const autolandDate = flight?.flight?.date_of_flight ? formatDisplayDate(flight.flight.date_of_flight) : '';

  /* ── SUB VIEWS ──────────────────────────────────────────── */

  if (view === 'flightData') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow" />POST FLIGHT</button>
        <div className="ogm-page-title">Flight Data</div>
        <button className="ogm-save-btn" onClick={handleSaveFlightData} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        <div className="fd-section">
          <div className="fd-row">
            <div className="fd-label">Date of Flight</div>
            <input className="form-input fd fd-input" readOnly value={f?.date_of_flight ? formatDisplayDateTime(f.date_of_flight) : ''} />
          </div>
          <div className="fd-row">
            <div className="fd-label">Flight Number</div>
            <input className="form-input fd fd-input" readOnly value={f?.flight_number || ''} />
          </div>
          <div className="fd-row">
            <div className="fd-label">From - To</div>
            <div className="fd-from-to">
              <input className="form-input fd fd-input" readOnly value={f?.from_airport || ''} />
              <span className="fd-separator">–</span>
              <input
                className="form-input fd fd-input"
                value={flightData.to_airport}
                onChange={e => setFlightData(d => ({ ...d, to_airport: formatIcaoInput(e.target.value) }))}
                maxLength={4}
              />
            </div>
          </div>
        </div>

        <div className="fd-oooi-section">
          <div className="fd-paired-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Time Off-block</div>
              <div className={getOooIInputClass(true, zuluTimes.time_off_block)}>
                <input type="text" className="form-input fd fd-time-input"
                  value={zuluTimes.time_off_block}
                  onChange={e => handleZuluChange('time_off_block', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_off_block', e)} />
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time TakeOff</div>
              <div className={getOooIInputClass(canEditTakeoff, zuluTimes.time_takeoff)}>
                <input type="text" className="form-input fd fd-time-input"
                  value={zuluTimes.time_takeoff}
                  onChange={e => handleZuluChange('time_takeoff', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_takeoff', e)}
                  disabled={!canEditTakeoff} />
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time On-block</div>
              <div className={getOooIInputClass(canEditOnBlock, zuluTimes.time_on_block)}>
                <input type="text" className="form-input fd fd-time-input"
                  value={zuluTimes.time_on_block}
                  onChange={e => handleZuluChange('time_on_block', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_on_block', e)}
                  disabled={!canEditOnBlock} />
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time Landing</div>
              <div className={getOooIInputClass(canEditLanding, zuluTimes.time_landing)}>
                <input type="text" className="form-input fd fd-time-input"
                  value={zuluTimes.time_landing}
                  onChange={e => handleZuluChange('time_landing', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_landing', e)}
                  disabled={!canEditLanding} />
              </div>
            </div>
          </div>

          <div className="fd-paired-grid fd-calc-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Block Time</div>
              <div className="fd-time-calc-box">
                {flightData.time_off_block && flightData.time_on_block
                  ? formatMinutesToHourMin((new Date(flightData.time_on_block) - new Date(flightData.time_off_block)) / 60000)
                  : ''}
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Flight Time</div>
              <div className="fd-time-calc-box">
                {flightData.time_takeoff && flightData.time_landing
                  ? formatMinutesToHourMin((new Date(flightData.time_landing) - new Date(flightData.time_takeoff)) / 60000)
                  : ''}
              </div>
            </div>
          </div>
        </div>

        <div className="fd-landing-section">
          <div className="fd-paired-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Full Stop Landing</div>
              <input type="number" className="form-input fd fd-landing-input"
                value={flightData.full_stop_landings}
                onChange={e => handleLandingCountChange('full_stop_landings', e.target.value)} />
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Push back this Sector</div>
              <div className="toggle-group fd-landing-toggle">
                <button type="button" className={`toggle-option ${!flightData.pushback ? 'active' : ''}`}
                  onClick={() => setFlightData(d => ({ ...d, pushback: false }))}>No</button>
                <button type="button" className={`toggle-option ${flightData.pushback ? 'active' : ''}`}
                  onClick={() => setFlightData(d => ({ ...d, pushback: true }))}>Yes</button>
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Touch & Go</div>
              <input type="number" className="form-input fd fd-landing-input"
                value={flightData.touch_and_go}
                onChange={e => handleLandingCountChange('touch_and_go', e.target.value)} />
            </div>
            <div />
          </div>

          <div className="fd-paired-grid fd-single-left">
            <div className="fd-paired-cell">
              <div className="fd-label">Total Landings</div>
              <div className="fd-time-calc-box fd-time-calc-left">{flightData.total_landings}</div>
            </div>
            <div />
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'arrivalFuel') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow" />POST FLIGHT</button>
        <div className="ogm-page-title">Arrival Fuel</div>
        <button className="ogm-save-btn" onClick={handleSaveArrivalFuel} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area ogm-fuel-page">
        <div className="fuel-capacity-info">
          <span>Max Fuel Capacity ({aircraft?.ac_type || 'Aircraft'}): </span>
          <strong>{maxFuelCapacity.toLocaleString()} kg</strong>
        </div>

        <div className="fuel-card">
          <div className="fuel-card-title">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={fuelUpliftIcon} alt="" className="ogm-icon-img" /></span>
            <span>Arrival Fuel (Remaining on Landing)</span>
          </div>

          <div className="fuel-grid-table">
            <div className="fuel-col-head">Tank</div>
            <div className="fuel-col-head">Left</div>
            <div className="fuel-col-head">Center</div>
            <div className="fuel-col-head">Right</div>
            <div className="fuel-col-head">Other</div>
            <div className="fuel-col-head">Total</div>

            <div className="fuel-row-head">Arrival Fuel</div>
            {FUEL_TANKS.map(tank => (
              <div key={`arrival-${tank}`} className={`fuel-cell-input ${isFieldValid(arrivalFuel[`arrival_fuel_${tank}`]) ? 'valid' : 'invalid'}`}>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-input"
                  value={arrivalFuel[`arrival_fuel_${tank}`]}
                  onChange={e => handleNumericInput(e.target.value, (val) => updateArrivalFuelField(`arrival_fuel_${tank}`, val))}
                  placeholder=""
                />
                <span>kg</span>
              </div>
            ))}
            <div className="fuel-total-box">{formatFuelValue(arrivalFuelTotal)} <span>kg</span></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'autoland') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow" />POST FLIGHT</button>
        <div className="ogm-page-title">Autoland Check</div>
        <button className="ogm-save-btn" onClick={handleSaveAutoland} disabled={saving || !hasAutolandReport || !autolandTimeIsValid || !autolandWindIsValid || !autolandTempIsValid}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        <div className="autoland-section-card">
          <div className="autoland-section-title">General</div>
          <div className="autoland-grid autoland-grid-general">
            <div className="autoland-field">
              <label className="autoland-label">A/C Reg / Flight Number</label>
              <div className="autoland-readonly-box">{autolandRegAndFlight}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Airport</label>
              <div className="autoland-readonly-box">{autolandAirport}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Captain</label>
              <div className="autoland-readonly-box">{captainName}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Date</label>
              <div className="autoland-readonly-box">{autolandDate}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Time</label>
              <div className={getAutolandFieldClass(autolandTimeIsValid)}>
                <input
                  type="text"
                  className="form-input autoland-input"
                  value={autoland.report.time}
                  onChange={(e) => updateAutolandReport('time', handleZuluTimeInput(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key !== 'Backspace') return;
                    const current = autoland.report.time || '';
                    const isCursorAtEnd = e.target.selectionStart === current.length && e.target.selectionEnd === current.length;
                    if (!current.endsWith('Z') || !isCursorAtEnd) return;
                    e.preventDefault();
                    const digits = current.replace(/\D/g, '').slice(0, 4);
                    const nextDigits = digits.slice(0, -1);
                    updateAutolandReport('time', handleZuluTimeInput(nextDigits));
                  }}
                />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">First Officer</label>
              <div className="autoland-readonly-box">{firstOfficerName}</div>
            </div>
          </div>
        </div>

        <div className="autoland-section-card">
          <div className="autoland-section-title">Data</div>
          <div className="autoland-grid autoland-grid-data">
            <div className="autoland-field">
              <label className="autoland-label">Runway</label>
              <div className={getAutolandFieldClass(autolandRunwayIsValid)}>
                <input
                  type="text"
                  className="form-input autoland-input"
                  value={autoland.report.runway}
                  onChange={(e) => updateAutolandReport('runway', formatAutolandRunwayInput(e.target.value))}
                />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Wind</label>
              <div className={getAutolandFieldClass(autolandWindIsValid)}>
                <input
                  type="text"
                  className="form-input autoland-input"
                  value={autoland.report.wind}
                  onChange={(e) => updateAutolandReport('wind', formatAutolandWindInput(e.target.value))}
                  onKeyDown={handleAutolandWindKeyDown}
                />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">QNH</label>
              <div className={getAutolandFieldClass(autolandQnhIsValid)}>
                <input
                  type="text"
                  className="form-input autoland-input"
                  value={autoland.report.qnh}
                  onChange={(e) => updateAutolandReport('qnh', formatAutolandQnhInput(e.target.value))}
                />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Temp</label>
              <div className={getAutolandFieldClass(autolandTempIsValid)}>
                <input
                  type="text"
                  className="form-input autoland-input"
                  value={autoland.report.temp}
                  onChange={(e) => updateAutolandReport('temp', formatAutolandTempInput(e.target.value))}
                  onKeyDown={handleAutolandTempKeyDown}
                />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">VIS/RVR</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.vis_rvr))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.vis_rvr} onChange={(e) => updateAutolandReport('vis_rvr', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">T/D Point</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.td_point))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.td_point} onChange={(e) => updateAutolandReport('td_point', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Alignment</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.alignment))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.alignment} onChange={(e) => updateAutolandReport('alignment', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Landing</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.landing))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.landing} onChange={(e) => updateAutolandReport('landing', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Tracking</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.tracking))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.tracking} onChange={(e) => updateAutolandReport('tracking', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Speed Control</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.speed_control))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.speed_control} onChange={(e) => updateAutolandReport('speed_control', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">A/C Dropout</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.ac_dropout))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.ac_dropout} onChange={(e) => updateAutolandReport('ac_dropout', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Other</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.other))}>
                <input type="text" className="form-input autoland-input" value={autoland.report.other} onChange={(e) => updateAutolandReport('other', e.target.value)} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Reasons</label>
              <div className={getAutolandFieldClass(hasValue(autoland.report.reasons))}>
                <input
                  type="text"
                  className="form-input autoland-input"
                  value={autoland.report.reasons}
                  onChange={(e) => updateAutolandReport('reasons', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'deicing') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow" />POST FLIGHT</button>
        <div className="ogm-page-title">De-Icing/Anti-Icing - Ground Mode</div>
        <button className="ogm-save-btn" onClick={handleSaveDeicing} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area deicing-scroll-area">
        {deicingRecords.map((record, index) => (
          <div className={`deicing-record-card anim-record-enter ${removingDeicingIndex === index ? 'anim-record-exit' : ''}`} key={`deicing-record-post-${index}`}>
            <div className="deicing-record-title">
              <div className="deicing-record-title-left">
                <img src={deicingIcon} alt="" className="deicing-record-title-icon" />
                <span>Record {index + 1}</span>
              </div>
              {index > 0 ? (
                <button
                  className="crew-remove-btn"
                  onClick={() => removeDeicingRecord(index)}
                  title="Remove this record"
                >
                  ✕
                </button>
              ) : (
                <span className="crew-remove-spacer" aria-hidden="true" />
              )}
            </div>
            <div className="deicing-grid">
              <div className="deicing-field">
                <label className="deicing-label">Fluid Type</label>
                <div className={`deicing-required-wrap ${record.fluid_type ? 'complete' : 'incomplete'}`}>
                  <div className="deicing-type-dropdown" onClick={e => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`deicing-type-trigger ${record.fluid_type ? 'has-value' : ''}`}
                      onClick={() => setDeicingFluidDropdownOpenIndex(open => (open === index ? null : index))}
                    >
                      <span className="deicing-type-trigger-main">
                        {record.fluid_type || ''}
                      </span>
                      <span
                        className={`deicing-type-trigger-arrow ${deicingFluidDropdownOpenIndex === index ? 'open' : ''}`}
                        aria-hidden="true"
                      >
                        ▾
                      </span>
                    </button>
                    <div className={`deicing-type-menu ${deicingFluidDropdownOpenIndex === index ? 'open' : 'closed'}`}>
                      <button
                        type="button"
                        className={`deicing-type-option ${!record.fluid_type ? 'active' : ''}`}
                        onClick={() => {
                          updateDeicingRecord(index, { fluid_type: '' });
                          setDeicingFluidDropdownOpenIndex(null);
                        }}
                      >
                        None
                      </button>
                      {DEICING_FLUID_TYPES.map(type => (
                        <button
                          key={type}
                          type="button"
                          className={`deicing-type-option ${record.fluid_type === type ? 'active' : ''}`}
                          onClick={() => {
                            updateDeicingRecord(index, { fluid_type: type });
                            setDeicingFluidDropdownOpenIndex(null);
                          }}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Mixture</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input deicing-input"
                    value={record.mixture}
                    onChange={e => {
                      const nextValue = handleDeicingNumericInput(e.target.value);
                      if (nextValue !== null) updateDeicingRecord(index, { mixture: nextValue });
                    }}
                  />
                  <span className="deicing-suffix">%</span>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Quantity</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input deicing-input"
                    value={record.quantity}
                    onChange={e => {
                      const nextValue = handleDeicingNumericInput(e.target.value);
                      if (nextValue !== null) updateDeicingRecord(index, { quantity: nextValue });
                    }}
                  />
                  <span className="deicing-suffix">l</span>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Start UTC</label>
                <input
                  type="text"
                  className="form-input deicing-input"
                  value={record.start_utc}
                  onChange={e => handleDeicingZuluChange(index, e.target.value)}
                  onKeyDown={e => handleDeicingZuluKeyDown(index, e)}
                />
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Holdover Min</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-input deicing-input"
                    value={record.holdover_min}
                    onChange={e => updateDeicingRecord(index, { holdover_min: handleDeicingMinutesInput(e.target.value) })}
                  />
                  <span className="deicing-suffix">min</span>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Holdover Max</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-input deicing-input"
                    value={record.holdover_max}
                    onChange={e => updateDeicingRecord(index, { holdover_max: handleDeicingMinutesInput(e.target.value) })}
                  />
                  <span className="deicing-suffix">min</span>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="fuel-actions">
          <button type="button" className={`fuel-add-btn anim-add-btn ${animateDeicingAddBtn ? 'anim-add-btn-active' : ''}`} onClick={addDeicingRecord}>Add New Record</button>
        </div>
      </div>
    </div>
  );

  if (view === 'delay') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow" />POST FLIGHT</button>
        <div className="ogm-page-title">Delay</div>
        <button className="ogm-save-btn" onClick={async () => {
          if (!delay.delay_code || !isDelayMinutesValid) return;
          setSaving(true);
          try {
            await addDelay(flightId, {
              delay_code: delay.delay_code,
              delay_description: delay.delay_description || null,
              delay_minutes: Number(delay.delay_minutes)
            });
            setDelay(prev => ({ ...prev, delay_description: '', delay_minutes: '' }));
            await loadFlight();
            navigateBackToMain();
          } catch (err) {
            console.error(err);
          }
          setSaving(false);
        }} disabled={saving || !delay.delay_code || !isDelayMinutesValid}>
          {saving ? 'Saving...' : 'Add Delay'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        <div className="delay-entry-card">
          <div className="delay-selected-code">
            {selectedDelayOption ? `${selectedDelayOption.code} (${selectedDelayOption.reason}) ${selectedDelayOption.description}` : ''}
          </div>

          <div className="delay-entry-field">
            <label className="delay-entry-label">Minutes</label>
            <div className={`delay-minutes-wrap ${isDelayMinutesValid ? 'complete' : 'incomplete'}`}>
              <input
                type="text"
                inputMode="numeric"
                className="form-input"
                value={delay.delay_minutes}
                onChange={e => setDelay(d => ({ ...d, delay_minutes: handleDelayMinutesInput(e.target.value) }))}
              />
            </div>
          </div>

          <div className="delay-entry-field">
            <label className="delay-entry-label">Description</label>
            <input
              className="form-input"
              value={delay.delay_description}
              onChange={e => setDelay(d => ({ ...d, delay_description: e.target.value }))}
            />
          </div>
        </div>

        {flight?.delays?.length > 0 && (
          <div className="delay-list">
            <h4 className="ogm-section-label">Added Delays</h4>
            {flight.delays.map(d => (
              <div key={d.id} className={`delay-item anim-record-enter ${removingDelayId === d.id ? 'anim-record-exit' : ''}`}>
                <div className="delay-info">
                  <span className="delay-code">{d.delay_code}</span>
                  <span className="delay-desc">{d.delay_description}</span>
                  <span className="delay-mins">{d.delay_minutes} min</span>
                </div>
                <button className="delay-remove-btn" disabled={removingDelayId !== null} onClick={() => handleRemoveDelay(d.id)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (view === 'aircraftStatus') return (
    <div className={viewContainerClass}>
      <AircraftStatusReport
        aircraftId={aircraft?.id}
        embeddedMode
        showFlyButton={false}
        onBack={navigateBackToMain}
        backLabel="POST FLIGHT"
        rootTitle="Aircraft Status"
        initialSection="main"
      />
    </div>
  );

  if (view === 'closedComplaints') return (
    <div className={viewContainerClass}>
      <AircraftStatusReport
        aircraftId={aircraft?.id}
        embeddedMode
        showFlyButton={false}
        onBack={navigateBackToMain}
        backLabel="POST FLIGHT"
        rootTitle="Closed Complaints"
        initialSection="closed"
      />
    </div>
  );

  if (view === 'cabinItems') return (
    <div className={viewContainerClass}>
      <AircraftStatusReport
        aircraftId={aircraft?.id}
        embeddedMode
        showFlyButton={false}
        onBack={navigateBackToMain}
        backLabel="POST FLIGHT"
        rootTitle="Cabin Items"
        initialSection="cabin"
      />
    </div>
  );

  /* ── MAIN VIEW ──────────────────────────────────────────── */

  const pilotItems = complaints.filter(c => c.type === 'Pilot').length;
  const maintenanceItems = complaints.filter(c => c.type === 'Maintenance' || c.category === 'Maintenance').length;
  const cabinItems = complaints.filter(c => c.type === 'Cabin').length;
  const totalStatus = complaints.length;
  const currentComplaints = complaints.filter(c => c.status === 'OPEN');
  const recentlyClosed = complaints.filter(c => c.status === 'CLOSED').length;
  const cabinOpen = complaints.filter(c => c.type === 'Cabin' && c.status === 'OPEN').length;
  const cabinAccepted = complaints.filter(c => c.type === 'Cabin' && c.status === 'ACCEPTED').length;
  const cabinDeferred = complaints.filter(c => c.type === 'Cabin' && c.status === 'DEFERRED').length;
  const cabinClosed = complaints.filter(c => c.type === 'Cabin' && c.status === 'CLOSED').length;

  return (
    <div className={viewContainerClass}>
      <div className="ogm-header">
        <div className="ogm-pill">Post Flight</div>
        <button className="ogm-accept-btn" onClick={() => { if (canCloseFlight) setCloseModalOpen(true); }} disabled={saving || !canCloseFlight}>Close Flight</button>
      </div>

      <div className="ogm-scroll-area">
        <h3 className="ogm-section-label">Flight Tasks</h3>

        <TaskRow label="Flight Data" isComplete={hasFlightData}
          statusText={!hasFlightData ? 'Incomplete Flight Data' : ''}
          onClick={() => openSubView('flightData')}
          iconSrc={flightDataIcon}
          iconClassName="ogm-icon-lg" />

        {/* Current Complaints */}
        <div className="ogm-item complete ogm-info-row">
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={openComplaintsIcon} alt="" className="ogm-icon-img" /></span>
            <span>Current Complaints</span>
          </div>
          <div className="ogm-right ogm-info-stats">
            <div className="ogm-stat">
              <span className="ogm-stat-label">Open</span>
              <span className="ogm-stat-value">{currentComplaints.length}</span>
            </div>
            <Chevron />
          </div>
        </div>

        <TaskRow
          label="Autoland"
          isComplete={hasAutolandComplete}
          statusText={!hasAutolandComplete ? 'Incomplete Autoland' : ''}
          onClick={() => {
            if (hasAutolandReport) openSubView('autoland');
          }}
          rightElement={(
            <div className="autoland-card-selector" onClick={e => e.stopPropagation()}>
              <button
                type="button"
                className={`autoland-card-btn ${autoland.status === AUTOLAND_STATUS.SUCCESSFUL ? 'active' : ''}`}
                onClick={() => setAutolandStatus(AUTOLAND_STATUS.SUCCESSFUL, { openReport: true })}
              >
                Successful <span className="autoland-card-btn-arrow" aria-hidden="true">›</span>
              </button>
              <button
                type="button"
                className={`autoland-card-btn ${autoland.status === AUTOLAND_STATUS.FAILED ? 'active' : ''}`}
                onClick={() => setAutolandStatus(AUTOLAND_STATUS.FAILED, { openReport: true })}
              >
                Failed <span className="autoland-card-btn-arrow" aria-hidden="true">›</span>
              </button>
              <button
                type="button"
                className={`autoland-card-btn ${autoland.status === AUTOLAND_STATUS.NO ? 'active' : ''}`}
                onClick={() => setAutolandStatus(AUTOLAND_STATUS.NO)}
              >
                No
              </button>
            </div>
          )}
          noChevron
          iconSrc={flightDataIcon}
        />

        <TaskRow label="Arrival Fuel" isComplete={hasArrivalFuel}
          statusText={!hasArrivalFuel ? 'Incomplete Arrival Fuel' : ''}
          onClick={() => openSubView('arrivalFuel')}
          iconSrc={fuelUpliftIcon} />

        <h3 className="ogm-section-label">Optional Tasks</h3>

        <TaskRow label="De-Icing/Anti-Icing" neutral onClick={() => openSubView('deicing')} iconSrc={deicingIcon} />

        {/* Delay Row */}
        <div className="ogm-item" onClick={() => { if (delay.delay_code) openSubView('delay'); }}>
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={delayIcon} alt="" className="ogm-icon-img" /></span>
            <span>Delay</span>
          </div>
          <div className="ogm-right">
            <div className="delay-dropdown" ref={delayDropdownRef} onClick={e => e.stopPropagation()}>
              <button
                type="button"
                className={`delay-dropdown-trigger ${delay.delay_code ? 'has-value' : ''}`}
                onClick={() => setDelayDropdownOpen(open => !open)}
              >
                <span className="delay-trigger-main">{selectedDelayLabel}</span>
                <span className={`delay-trigger-arrow ${delayDropdownOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
              </button>
              <div className={`delay-dropdown-menu ${delayDropdownOpen ? 'open' : 'closed'}`}>
                <button
                  type="button"
                  className={`delay-dropdown-option ${!delay.delay_code ? 'active' : ''}`}
                  onClick={() => {
                    setDelay({
                      delay_code: '',
                      delay_description: '',
                      delay_minutes: ''
                    });
                    setDelayDropdownOpen(false);
                  }}
                >
                  <span className="delay-option-code">None</span>
                </button>
                {DELAY_CODE_OPTIONS.map(option => (
                  <button
                    key={option.code}
                    type="button"
                    className={`delay-dropdown-option ${delay.delay_code === option.code ? 'active' : ''}`}
                    onClick={() => {
                      setDelay({
                        delay_code: option.code,
                        delay_description: '',
                        delay_minutes: ''
                      });
                      setDelayDropdownOpen(false);
                      openSubView('delay');
                    }}
                  >
                    <span className="delay-option-code">{`${option.code} (${option.reason})`}</span>
                    <span className="delay-option-desc">{option.description}</span>
                  </button>
                ))}
              </div>
            </div>
            {delay.delay_code ? <Chevron /> : null}
          </div>
        </div>

        <h3 className="ogm-section-label">For Your Information</h3>

        {/* Aircraft Status */}
        <div className="ogm-item ogm-info-row" onClick={() => openSubView('aircraftStatus')}>
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={aircraftStatusIcon} alt="" className="ogm-icon-img ogm-icon-lg" /></span>
            <span>Aircraft Status</span>
          </div>
          <div className="ogm-right ogm-info-stats">
            <div className="ogm-stat">
              <span className="ogm-stat-label">Pilot Items</span>
              <span className="ogm-stat-value">{pilotItems}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Maintenance Items</span>
              <span className="ogm-stat-value">{maintenanceItems}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Cabin Items</span>
              <span className="ogm-stat-value">{cabinItems}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Status</span>
              <span className="ogm-stat-value">{totalStatus}</span>
            </div>
            <Chevron />
          </div>
        </div>

        {/* Closed Complaints */}
        <div className="ogm-item ogm-info-row" onClick={() => openSubView('closedComplaints')}>
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={openComplaintsIcon} alt="" className="ogm-icon-img ogm-icon-lg" /></span>
            <span>Closed Complaints</span>
          </div>
          <div className="ogm-right ogm-info-stats">
            <div className="ogm-stat">
              <span className="ogm-stat-label">Recently Closed</span>
              <span className="ogm-stat-value">{recentlyClosed}</span>
            </div>
            <Chevron />
          </div>
        </div>

        {/* Cabin Items */}
        <div className="ogm-item ogm-info-row" onClick={() => openSubView('cabinItems')}>
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={cabinItemsIcon} alt="" className="ogm-icon-img" /></span>
            <span>Cabin Items</span>
          </div>
          <div className="ogm-right ogm-info-stats">
            <div className="ogm-stat">
              <span className="ogm-stat-label">Open</span>
              <span className="ogm-stat-value">{cabinOpen}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Accepted</span>
              <span className="ogm-stat-value">{cabinAccepted}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Deferred</span>
              <span className="ogm-stat-value">{cabinDeferred}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Recently Closed</span>
              <span className="ogm-stat-value">{cabinClosed}</span>
            </div>
            <Chevron />
          </div>
        </div>

        <button className="btn btn-warning" onClick={handleRevoke}>Revoke Aircraft Acceptance</button>
        <button className="btn btn-danger" onClick={handleCancel}>Cancel Flight</button>
      </div>

      {closeModalOpen && (
        <CommanderConfirmationModal
          title="Commander's Closure"
          actionLabel="Close Flight"
          pending={saving}
          onClose={() => { if (!saving) setCloseModalOpen(false); }}
          onConfirm={handleClose}
          confirmationText="Check here to confirm that all required post-flight data has been entered. OOOI data, arrival fuel, autoland status/report where applicable, aircraft status and operational entries have been reviewed and are satisfactory for closing this flight."
        />
      )}
    </div>
  );
};

export default PostFlightMode;


